import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import cx from 'classnames'
import { useFiltersModalStore } from 'store/filtersModalStore'
import { shallow } from 'zustand/shallow'

import {
  Box,
  Button,
  DisplayTableColumn,
  Flex,
  Icon as DDSIcon,
  Icon,
  Table,
  TableColumn,
  TableColumnWithCustomValue,
  Text,
  Tooltip,
  useTableRef,
  useTableState
} from '@driftt/dds'
import { faCircleInfo } from '@driftt/dds.icons/light/faCircleInfo'
import { faFilter } from '@driftt/dds.icons/light/faFilter'

import AccountFitTooltip from 'components/AccountFitTooltip'
import AccountScoreTrendGraph from 'components/AccountScoreTrendGraph'
import ActivityContentToolTip from 'components/ActivityContentTooltip'
import { AccountActivityAccountAvatar } from 'components/Avatars'
import EngagementScoreIndicator, {
  EngagementScoreIndicatorVariant
} from 'components/EngagementScoreIndicator'
import EngagementScoreTooltip from 'components/EngagementScoreTooltip'
import { driftErrorStateBuilder } from 'components/States'
import { AccountDrawerContext } from 'context/accountDrawerContext'
import { SortContext } from 'context/sortContext'
import { useAccounts } from 'hooks/useAccounts'
import { useAppSelector } from 'hooks/useRedux'
import { AccountActivity, AccountActivitySortValue } from 'modules/accountActivity/domain'
import { AccountOpportunityContext } from 'pages/ActivityDashboard/AccountOpportunityContext'
import { getCurrentOrg } from 'selectors'
import { isAccountFitEnabled } from 'utils/features'
import { segmentTrack, TrackType } from 'utils/tracking'

import ActivityInfo from './ActivityInfo'
import LastTouch from './LastTouch'
import ScoreGraphTooltip from './ScoreGraphTooltip'

import styles from './styles.module.css'

type AccountActivityProps = {
  accountActivity: AccountActivity
}

type NavigationWrapperProps = {
  accountId: string
  accountName: string | undefined
  children: React.ReactNode
}

type AccountEngagementTableRow = {
  data: AccountActivity
  i: number
}

export const MAX_TABLE_RECORDS = 10_000

const BASE_TABLE_COLUMNS:
  | TableColumn<AccountEngagementTableRow>[]
  | TableColumnWithCustomValue<AccountEngagementTableRow>[]
  | DisplayTableColumn<AccountEngagementTableRow>[] = [
  {
    id: 'ACCOUNT_NAME',
    header: 'Account',
    value: 'data.account.accountId',
    size: 170,
    cell: props => <AccountInfo accountActivity={props.data} />
  },
  {
    id: 'ENGAGEMENT_SCORE',
    header: 'Engagement',
    size: 50,
    value: 'data.normalizedEngagementScore',
    cell: props => (
      <span className="ai-engagement-onboarding">
        <EngagementScoreTooltip accountActivity={props.data} boundaryRef={null} />
      </span>
    )
  },
  {
    id: 'TRENDING',
    size: 50,
    value: 'data.accountScoredActivity',
    header: () => <span className={styles.tableHeader}>Trend</span>,
    cell: props => <ScoreTrend accountActivity={props.data} />
  },
  {
    id: 'scoreGraph',
    size: 100,
    header: () => (
      <Flex alignItems="center" justifyContent="center" gap={2}>
        <span>Engagement Graph</span>
        <ScoreGraphTooltip />
      </Flex>
    ),
    value: 'data.accountScoredActivity',
    disableSort: true,
    cell: props => (
      <AccountScoreTrendGraph
        accountActivity={props.data}
        score={props.data.accountScoredActivity}
      />
    )
  },
  {
    id: 'MOST_RECENT',
    header: 'Most Recent Activity',
    value: 'data.mostRecentActivity',
    cell: props => <MostRecentActivity accountActivity={props.data} />
  },
  {
    id: 'LAST_TOUCHED',
    header: () => (
      <Flex alignItems="center" justifyContent="center" gap={2}>
        <span>Last Touch</span>
        <Tooltip content="The most recent outbound action in the last 2 weeks.">
          <Icon
            css={{
              width: '10px',
              height: '10px',
              cursor: 'pointer'
            }}
            icon={faCircleInfo}
          />
        </Tooltip>
      </Flex>
    ),
    value: 'data.driftSfdcTask.insertedAt',
    cell: props => <LastTouch accountActivity={props.data} />
  }
]

const DEFAULT_TABLE_SORT = { defaultSort: [{ id: 'TRENDING', desc: true }] }

export const NavigationWrapper = ({ accountId, accountName, children }: NavigationWrapperProps) => {
  const { setIsOpen: setIsDrawerOpen, setAccountId: setSelectedAccountId } = useContext(
    AccountDrawerContext
  )

  const navigateToAccountDetails = () => {
    setIsDrawerOpen(true)
    setSelectedAccountId(accountId)
  }

  return (
    <button
      aria-label={`navigates to ${accountName} account details page`}
      className={styles.navigationWrapper}
      onClick={navigateToAccountDetails}>
      {children}
    </button>
  )
}

const AccountInfo = ({ accountActivity }: AccountActivityProps) => {
  const refElement = useRef<HTMLDivElement>(null)

  return (
    <NavigationWrapper
      accountId={accountActivity.accountId}
      accountName={accountActivity.companyData?.name}>
      <Flex alignItems="center">
        <AccountActivityAccountAvatar
          imageUrl={accountActivity.companyData?.logo}
          name={accountActivity.companyData?.name}
          inCrm={!!accountActivity.account}
        />

        <ActivityContentToolTip
          data={accountActivity.companyData}
          inCrm={!!accountActivity.account}>
          <Box ref={refElement}>
            <Text
              variant="bodyBold"
              css={{
                color: '$blue8',
                maxWidth: '130px' // desired max width of column - width of icon = 170-40 = 130
              }}
              truncate>
              {accountActivity.account?.name ||
                accountActivity.companyData?.name ||
                accountActivity.companyData?.domain ||
                'Unnamed account'}
            </Text>
          </Box>
        </ActivityContentToolTip>
      </Flex>
    </NavigationWrapper>
  )
}

const ScoreTrend = ({ accountActivity }: AccountActivityProps) => {
  const trend = accountActivity.accountScoredActivity?.trend || 0
  const normalizedScore = accountActivity.accountScoredActivity?.historicalScores?.shift() || 0
  const score = Math.min(Math.round(normalizedScore), 5)
  const scoreIsNeutral = score - Math.round(Math.abs(trend)) === score

  let direction: EngagementScoreIndicatorVariant = 'neutral'
  let styleTrend = styles.neutralTrend

  if (trend > 0 && !scoreIsNeutral) {
    direction = 'positive'
    styleTrend = styles.positiveTrend
  } else if (trend < 0 && !scoreIsNeutral) {
    direction = 'negative'
    styleTrend = styles.negativeTrend
  }

  return (
    <NavigationWrapper
      accountId={accountActivity.accountId}
      accountName={accountActivity.companyData?.name}>
      <div className={cx(styles.scoreTrend, styleTrend)}>
        <EngagementScoreIndicator variant={direction} />
        {direction !== 'neutral' && (
          <span
            className={cx(
              styles.scoreTrend,
              direction === 'negative' ? styles.negativeLabel : styles.positiveLabel
            )}>
            {trend > 0 ? '+' : ''}
            {trend.toFixed()}
          </span>
        )}
      </div>
    </NavigationWrapper>
  )
}

const MostRecentActivity = ({ accountActivity }: AccountActivityProps) => {
  return (
    <ActivityInfo
      topActivities={accountActivity.accountScoredActivity?.topActivities}
      activity={accountActivity.mostRecentActivity}
    />
  )
}

type NoDataContainerProps = {
  noSearch: boolean
  setShowFiltersMenu: (showFiltersMenu: boolean) => void
}

export const getEmptyState = ({ noSearch, setShowFiltersMenu }: NoDataContainerProps) => {
  if (noSearch) {
    return {
      title: 'Apply or remove filters to see accounts here.',
      subTitle: `If you don't own accounts in your CRM, define which accounts you see with firmographic filters.`,
      action: (
        <Button
          size="small"
          className={styles.openFiltersButton}
          variant="secondary"
          onClick={() => {
            setShowFiltersMenu(true)
          }}>
          <DDSIcon icon={faFilter} />
          Filter
        </Button>
      )
    }
  }

  return {
    title: `We couldn't find any accounts matching your search.`,
    subTitle: 'Try refining your search terms to see accounts here.'
  }
}

type AccountEngagementTableProps = {
  setDataLength?: (length: number | undefined) => void
  setResultSize?: (length: number) => void
}

const AccountEngagementTable = ({ setDataLength, setResultSize }: AccountEngagementTableProps) => {
  const org = useSelector(getCurrentOrg)
  const { opportunities } = useContext(AccountOpportunityContext)

  const { toggleModal } = useFiltersModalStore(
    state => ({
      toggleModal: state.toggleModal
    }),
    shallow
  )
  const tableRef = useTableRef()
  const [hasFiredLoadTracking, setHasFiredLoadTracking] = useState<boolean>(false)
  const { searchQuery, activeFilterId, defaultFilterId, filterGroupsByPage } = useAppSelector(
    ({ filters }) => filters
  )

  const accountFitIsEnabled = isAccountFitEnabled(org)

  useEffect(() => {
    setHasFiredLoadTracking(false)
  }, [activeFilterId])
  const ref = useRef<HTMLDivElement>(null)

  const { accountsSort, setAccountsSort, accountsDescending, setAccountsDescending } = useContext(
    SortContext
  )

  const { tableState, handleTableStateChange } = useTableState<AccountEngagementTableRow>({
    defaultPagination: {
      pageIndex: 0,
      pageSize: 10
    }
  })

  useEffect(() => {
    if (tableState.sorting.length > 0) {
      if (accountsSort !== tableState.sorting[0].id) {
        setAccountsSort(tableState.sorting[0].id as AccountActivitySortValue)
      }
      if (accountsDescending !== tableState.sorting[0].desc) {
        setAccountsDescending(tableState.sorting[0].desc)
      }
    }
  }, [
    tableState.sorting,
    setAccountsSort,
    setAccountsDescending,
    hasFiredLoadTracking,
    accountsSort,
    accountsDescending
  ])

  const {
    data,
    resultSize,
    isLoadingMore,
    isLoadingInitialData,
    hasLoadedInitialData,
    countData,
    error
  } = useAccounts({
    pageSize: tableState.pagination.pageSize,
    pageCursor: tableState.pagination.pageIndex
      ? tableState.pagination.pageIndex * tableState.pagination.pageSize
      : tableState.pagination.pageIndex,
    opportunities
  })

  useEffect(() => {
    if (!hasFiredLoadTracking && hasLoadedInitialData) {
      segmentTrack(TrackType.ACCOUNTS_TABLE_LOADED, {
        resultSize: resultSize ?? 0,
        countData,
        activeFilterId,
        defaultFilter: activeFilterId === defaultFilterId,
        filterGroups: filterGroupsByPage['ACCOUNTS']
      })
      tableRef.current &&
        tableRef.current.reset({
          pagination: {
            pageIndex: 0,
            pageSize: 10
          }
        })
      setHasFiredLoadTracking(true)
    }
  }, [
    hasFiredLoadTracking,
    hasLoadedInitialData,
    resultSize,
    activeFilterId,
    defaultFilterId,
    filterGroupsByPage,
    tableRef,
    countData
  ])

  useEffect(() => {
    if (setDataLength) {
      const dataLength = isLoadingInitialData ? 0 : countData
      setDataLength(dataLength)
    }
  }, [countData, setDataLength, isLoadingInitialData])

  useEffect(() => {
    if (setResultSize) {
      setResultSize(data?.length ?? 0)
    }
  }, [setResultSize, data])

  const columns: TableColumn<AccountEngagementTableRow>[] = useMemo(
    () =>
      accountFitIsEnabled
        ? [
            BASE_TABLE_COLUMNS[0],
            {
              id: 'FIT_SCORE',
              header: 'Account Fit',
              size: 80,
              value: 'data.accountFitScore',
              cell: (props: AccountEngagementTableRow) => (
                <AccountFitTooltip
                  accountFitScore={props.data.accountFitScore}
                  companyData={props.data.companyData}
                  scoreBreakdown={props.data.featureWeights}
                />
              ),
              disableSort: true
            },
            ...BASE_TABLE_COLUMNS.slice(1)
          ]
        : BASE_TABLE_COLUMNS,
    [accountFitIsEnabled]
  )

  const emptyState = useMemo(
    () =>
      getEmptyState({
        noSearch: !searchQuery || (!!searchQuery && searchQuery.length === 0),
        setShowFiltersMenu: () => toggleModal(true)
      }),
    [searchQuery, toggleModal]
  )

  const ErrorComponent = driftErrorStateBuilder('dashboard data')

  const rows = useMemo(() => data.map((data, i) => ({ data, i })), [data])

  if (error) {
    return (
      <Box css={{ padding: '32px 0' }}>
        <ErrorComponent />
      </Box>
    )
  }
  return (
    <Box
      ref={ref}
      css={{
        padding: '0',
        overflow: 'hidden',
        height: '100%',
        boxShadow: 'none',
        '& tbody tr': {
          height: '56px',
          '& td': {
            paddingTop: '0',
            paddingBottom: '0'
          }
        }
      }}>
      <Table<AccountEngagementTableRow>
        rows={rows}
        disableBoxShadow
        tableRef={tableRef}
        columns={columns}
        sorting={DEFAULT_TABLE_SORT}
        onStateChange={handleTableStateChange}
        isLoading={isLoadingInitialData || isLoadingMore}
        pagination={{
          rowTotal: Math.min(countData ?? 0, MAX_TABLE_RECORDS),
          defaultPagination: tableState.pagination
        }}
        emptyState={emptyState}
      />
    </Box>
  )
}

export default AccountEngagementTable
